import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Country } from '@electricjs/arc';
import { ABMEnrollmentStatusEnum } from '@electricjs/core_entity-client';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import {
  useGetOrganizationAdditionalInfoQuery,
  useGetOrganizationResellersQuery,
} from '@/redux/slices/organizationApiSlice';

const CANADA_COUNTRY_CODE: Country = 'CA';

// Mirrors ABMConnectedMessage's `showCanadaResellerBanner` condition, so
// checkout can be blocked while that banner is still visible/unconfirmed.
const useIsCaResellerUnconfirmed = (storeCountry?: string) => {
  const organizationId = useGetOrganizationId();
  const { abmCanadaResellerId } = useFlags();

  const {
    data: orgAdditionalInfoData,
    isFetching: isFetchingOrgAdditionalInfoData,
  } = useGetOrganizationAdditionalInfoQuery({ organizationId });

  const shouldCheckCaReseller =
    Boolean(abmCanadaResellerId) &&
    storeCountry === CANADA_COUNTRY_CODE &&
    orgAdditionalInfoData?.abmStatus === ABMEnrollmentStatusEnum.Connected;

  const { data: resellers, isFetching: isFetchingResellers } =
    useGetOrganizationResellersQuery(
      shouldCheckCaReseller ? { organizationId } : skipToken
    );

  if (!shouldCheckCaReseller) {
    return false;
  }

  // While loading, treat as unconfirmed to prevent premature checkout
  if (isFetchingOrgAdditionalInfoData || isFetchingResellers) {
    return true;
  }

  const hasCaReseller =
    resellers?.some(reseller => reseller.countryCode === CANADA_COUNTRY_CODE) ??
    false;

  return !hasCaReseller;
};

export default useIsCaResellerUnconfirmed;
